import { useMemo, useState, useCallback } from 'react';

import { Typography, Stack, Badge, styled, BadgeProps } from '@mui/material';

import parse from 'html-react-parser';
import { makeStyles } from 'tss-react/mui';

import Accordion from '@/components/accordion';
import Card from '@/components/card';
import { Posts } from '@/models/posts';
import { msToPrettyTimestamp, msToSeconds } from '@/utils/datesAndTime';

const useStyles = makeStyles()((theme) => ({
  highlighted: {
    background: theme.palette.grey[100],
    borderLeft: `2px solid ${theme.palette.primary.main}`,
    paddingLeft: 5,
    cursor: 'pointer'
  },
  notHighlighted: {
    cursor: 'pointer'
  }
}));

interface TranscriptLineProps {
  line: Posts.TranscriptLine;
  highlightedSeconds: number | null;
  setHighlightedSeconds: (seconds: number | null) => void;
  setVideoPlaybackStart: (seconds: number) => void;
  setVideoLastClick: (ts: number) => void;
}

const TranscriptLine = ({
  line,
  highlightedSeconds,
  setHighlightedSeconds,
  setVideoPlaybackStart,
  setVideoLastClick
}: TranscriptLineProps) => {
  const { classes } = useStyles();
  const prettyTimestamp = msToPrettyTimestamp(line.timestamp);
  const lineSeconds = msToSeconds(line.timestamp);

  const isHighlighted = highlightedSeconds === lineSeconds;

  const handleTimestampClick = useCallback(() => {
    if (isHighlighted) {
      setHighlightedSeconds(null);
    } else {
      setHighlightedSeconds(lineSeconds);
      setVideoPlaybackStart(lineSeconds);
      setVideoLastClick(Date.now());
    }
  }, [
    isHighlighted,
    lineSeconds,
    setHighlightedSeconds,
    setVideoPlaybackStart,
    setVideoLastClick
  ]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      overflow="visible"
      padding={0.25}
      className={isHighlighted ? classes.highlighted : classes.notHighlighted}
      onClick={handleTimestampClick}
    >
      <Typography
        justifyContent="center"
        color="primary"
        fontSize={12}
        width={35}
        fontWeight={500}
      >
        {prettyTimestamp}
      </Typography>
      <Typography variant="body2" fontSize={12}>
        {parse(line.content)}
      </Typography>
    </Stack>
  );
};

interface TranscriptStackProps {
  transcript?: Posts.TranscriptLine[];
  highlightedSeconds: number | null;
  setHighlightedSeconds: (seconds: number | null) => void;
  setVideoPlaybackStart: (seconds: number) => void;
  setVideoLastClick: (ts: number) => void;
}

const TranscriptStack = ({
  transcript,
  highlightedSeconds,
  setHighlightedSeconds,
  setVideoPlaybackStart,
  setVideoLastClick
}: TranscriptStackProps) => (
  <Stack maxHeight={332} overflow="auto" gap={0.25}>
    {transcript?.map((line) => (
      <TranscriptLine
        key={line.timestamp}
        line={line}
        highlightedSeconds={highlightedSeconds}
        setHighlightedSeconds={setHighlightedSeconds}
        setVideoPlaybackStart={setVideoPlaybackStart}
        setVideoLastClick={setVideoLastClick}
      />
    ))}
  </Stack>
);

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: -15,
    top: 12
  }
}));

interface TranscriptBadgeProps {
  mediaItem: Posts.Media;
  index: number;
  activeIndex: number;
}

const TranscriptBadge = ({
  mediaItem,
  index,
  activeIndex
}: TranscriptBadgeProps) => {
  const isSelected = activeIndex === index;

  return (
    <StyledBadge
      badgeContent={mediaItem.transcript?.length ?? 0}
      color="primary"
    >
      <Typography
        variant="body2"
        sx={{
          transition: 'all 0.15s ease',
          ...(isSelected && {
            fontWeight: 600,
            textDecoration: 'underline'
          })
        }}
      >
        {`Item ${index + 1}`}
      </Typography>
    </StyledBadge>
  );
};

interface CreatorPostTranscriptProps {
  media: Posts.Media[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  videoPlaybackStart?: number;
  videoLastClick: string | number;
  setVideoPlaybackStart: (seconds: number) => void;
  setVideoLastClick: (ts: number) => void;
  resetMarkers: () => void;
}

const CreatorPostTranscript = ({
  media,
  activeIndex,
  setActiveIndex,
  setVideoPlaybackStart,
  setVideoLastClick,
  resetMarkers
}: CreatorPostTranscriptProps) => {
  const [highlightedSeconds, setHighlightedSeconds] = useState<number | null>(
    null
  );

  const hasAtLeastOneTranscript = useMemo(
    () => media.some((m) => m.transcript && m.transcript.length > 0),
    [media]
  );

  const handleAccordionChange = useCallback(
    (index: number) => {
      setActiveIndex(index);
      setHighlightedSeconds(null);
      resetMarkers();
    },
    [setActiveIndex, resetMarkers]
  );

  if (!hasAtLeastOneTranscript) return null;

  const sharedStackProps = {
    highlightedSeconds,
    setHighlightedSeconds,
    setVideoPlaybackStart,
    setVideoLastClick
  };

  const renderBody = () => {
    if (media.length === 1) {
      return (
        <TranscriptStack
          transcript={media[0].transcript}
          {...sharedStackProps}
        />
      );
    }

    return (
      <Stack>
        {media.map((mediaItem, index) => {
          const hasTranscript = (mediaItem.transcript?.length ?? 0) > 0;
          const isExpanded = index === activeIndex && hasTranscript;

          return (
            <Accordion
              key={mediaItem.mediaPath}
              id={mediaItem.mediaPath}
              title={
                <TranscriptBadge
                  mediaItem={mediaItem}
                  index={index}
                  activeIndex={activeIndex}
                />
              }
              handleChange={() => handleAccordionChange(index)}
              isExpanded={isExpanded}
              condensed
            >
              <TranscriptStack
                transcript={mediaItem.transcript}
                {...sharedStackProps}
              />
            </Accordion>
          );
        })}
      </Stack>
    );
  };

  return (
    <Card
      sx={{ marginBottom: 2 }}
      isCollapsible
      title="Audio Transcript"
      content={renderBody()}
    />
  );
};

export default CreatorPostTranscript;
